/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, toJS } from "mobx";
import { getViewMode } from "selectors/viewModeSelectors";
import { docViewModes, rhpDropinIds } from "constants/enums";
import { CLEAR_QUEUE_EVENTS } from "api/events/clearAJSQueueEvents";
import { pagesInView, thumbnailsInView } from "selectors/pagedViewSelectors";
import { apiFactory } from "api/external/ApiFactory";
import { publicApiNames } from "publicApis/constants";
import hcThemeProvider from "../highContrast/HCThemeProvider";
import { overlayEvents } from "overlays/OverlayEvents";

class HighContrastStore {
  @observable isHCFlagEnabled;
  @observable colorPalleteObservable;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.isHCFlagEnabled && hcThemeProvider.removeChangeListener(this.setColorPallete);
    this.isHCFlagEnabled = false;
    this.colorPalleteObservable = null;
  }

  @action.bound
  async setHCFlagEnabled(isHCFlagEnabled) {
    this.isHCFlagEnabled = isHCFlagEnabled;
    await hcThemeProvider.init();
    this.setColorPallete(hcThemeProvider.getColorPallete());
    // To skip duplicate call to getRendition api(by HighContrastStore and RenderingManager), reloadView is not called at this time
    hcThemeProvider.addChangeListener(this.setColorPallete);
  }

  get colorPallete() {
    return toJS(this.colorPalleteObservable);
  }

  @action.bound
  async setColorPallete(newColorPallete) {
    if (JSON.stringify(newColorPallete) === JSON.stringify(this.colorPallete)) return;
    this.colorPalleteObservable = newColorPallete;
    await this.reloadView();
  }

  async reloadView() {
    if (this.__shouldReloadPages()) {
      this.reloadPages();
    }
    const shouldReloadThumbnails = await this.__shouldReloadThumbnails();
    if (shouldReloadThumbnails) {
      this.reloadThumbnails();
    } else {
      this.stores.documentStore.unloadAllThumbnails();
    }
  }

  __shouldReloadPages() {
    const viewMode = getViewMode(this.stores);
    if (
      viewMode === docViewModes.NON_PDF ||
      viewMode === docViewModes.NO_PREVIEW ||
      viewMode === docViewModes.UNKNOWN
    ) {
      return false;
    }
    return true;
  }

  reloadPages() {
    this.stores.documentStore.clearLoadPageRequests({
      evtName: CLEAR_QUEUE_EVENTS.HC_THEME_CHANGE,
      colorPallete: this.colorPallete
    });
    const pagesInViewObj = pagesInView(this.stores);
    this.stores.documentStore.reloadPages(pagesInViewObj);
    const hcColorPalette = this.colorPallete;
    overlayEvents.trigger("hcThemeChange", hcColorPalette);
  }

  async __shouldReloadThumbnails() {
    const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
    if (rhpApis && rhpApis[publicApiNames.RHP]) {
      const activeDropin = await rhpApis[publicApiNames.RHP].getConfig();
      if (activeDropin && activeDropin.dropinId && activeDropin.dropinId === rhpDropinIds.THUMBNAILS) {
        return true;
      }
    }
    return false;
  }

  reloadThumbnails() {
    const thumbnailsInViewObj = thumbnailsInView(this.stores);
    this.stores.documentStore.reloadThumbnails(thumbnailsInViewObj);
  }
}

export { HighContrastStore };
